import { supabase } from '@/integrations/supabase/client';
import { hasGoogleToken, openGoogleAuthPopup } from '@/lib/googleAuth';

export interface ExportCampaignInfluencersParams {
  campaignId: string;
  columns: string[];
  orgId: string;
}

export async function exportCampaignInfluencersToGoogleSheet(
  params: ExportCampaignInfluencersParams
): Promise<string> {
  const { campaignId, columns, orgId } = params;

  if (!campaignId) throw new Error('Campaign ID is required');
  if (!orgId) throw new Error('Organization ID is required');

  const isConnected = await hasGoogleToken();
  if (!isConnected) {
    await openGoogleAuthPopup();
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('No active session. Please sign in.');

  const invokeExport = async () => {
    return supabase.functions.invoke('export-campaign-influencers-sheet', {
      body: {
        campaignId,
        columns,
        orgId,
      },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
  };

  let { data, error } = await invokeExport();

  let errorMsg = error?.message || '';
  let errorCode = data?.error || '';
  if (error && 'context' in (error as any)) {
    try {
      const errorBody = await (error as any).context.json();
      if (errorBody?.error) errorCode = errorBody.error;
      if (errorBody?.message) errorMsg = errorBody.message;
    } catch {
      // ignore json parse error
    }
  }

  // Self-healing: if token expired, revoked, or not connected, delete stale token, re-auth, and retry once
  if (
    errorCode === 'NOT_CONNECTED' ||
    errorMsg.includes('expired or revoked') ||
    errorMsg.includes('Token refresh failed') ||
    errorMsg.includes('not connected Google account')
  ) {
    try {
      await supabase.from('google_oauth_tokens').delete().eq('user_id', session.user.id);
    } catch {
      // ignore
    }

    await openGoogleAuthPopup();

    const retryResult = await invokeExport();
    data = retryResult.data;
    error = retryResult.error;

    if (error && 'context' in (error as any)) {
      try {
        const errorBody = await (error as any).context.json();
        if (errorBody?.message) errorMsg = errorBody.message;
      } catch {
        // ignore
      }
    }
  }

  if (error || !data?.spreadsheetUrl) {
    throw new Error(errorMsg || 'Failed to export assigned influencers to Google Sheets');
  }

  return data.spreadsheetUrl;
}
